import { image as imageConfig } from '@config';
import Base64 from 'assets/js/util/base64';

const { isDataURL } = require('../../../../helpers/dataUrl');
const ImagesFactory = require('../../../factory/images');

const ElementSetter = require('./abstract/element');

const pendingExternalUploads = new Set();
const pendingS3Urls = new Map();

module.exports = ImageSetter;

function ImageSetter () {
  this.handledAttributes = _.union(
    ['source', 'flipped'],
    this.handledAttributes
  );
}

ImageSetter.prototype = new ElementSetter();
ImageSetter.prototype.constructor = ImageSetter;

ImageSetter.prototype.updateThumbnail = async function (element, data) {
  const imageData = INKIVE.ImagesFactory.getImageOriginalData(data.source.url);
  const size = getImageSize(imageData, data);
  const url = getImageUrl(imageData, data);

  if (!size.width || !size.height) {
    const originalSize = await new Promise(resolve => {
      INKIVE.ImagesFactory.getImageOriginalSizeData(url, resolve);
    });
    size.width = originalSize.width;
    size.height = originalSize.height;
  }

  INKIVE.EditorFacade.canvas.runExtensions('addLayoutImage', {
    elem: element,
    data: {
      url,
      width: size.width,
      height: size.height
    }
  });

  function getImageSize (imageData, data) {
    const width = imageData?.version?.thumb_width || imageData?.version?.original_width || imageData?.original_width || data?.size?.width;
    const height = imageData?.version?.thumb_height || imageData?.version?.original_height || imageData?.original_height || data?.size?.height;

    return { width, height };
  }

  function getImageUrl (imageData, data) {
    return imageData?.version?.thumb || imageData?.thumb || data.source.url;
  }
};

ImageSetter.prototype.updateSource = function (element, data) {
  data.external = typeof data.source.external_resize === 'undefined'
    ? this.isExternal(data)
    : data.source.external_resize;

  if (data.external && !data.source.skip_thumbnail) {
    this.updateThumbnail(element, data);
  }

  const useSmartResize = typeof data.source.smart_load === 'undefined'
    ? true
    : data.source.smart_load;

  data.external && this.resizeExternalPhoto(element, data);
  this.removeFlippedAttributesIfNewImage(element, data);
  this.removeNoPlaceholderClassIfReplacedImage(element, data);

  if (!data.external) {
    if (data.external_source === INKIVE.C.SHUTTERSTOCK) {
      this.processSstkProImage(data, element);
      return;
    } else if (data.source.base_64) {
      this.updateSourceBase64(element, data);
    } else {
      const skipHistory = useSmartResize;

      if (!useSmartResize || data.source.preload) {
        this.updateSourceUrl(element, data, skipHistory);
      }
      useSmartResize && this.onVersionData(element, data);
    }
  }

  const source = _.extend(_.clone(data.source), {
    url: !isDataURL(data.source.url) && data.source.url
  });

  this.refreshPlaceholderButtons(element, data);

  this.trigger('source', element.getAttribute('id'), { source });
};

ImageSetter.prototype.processSstkProImage = async (data, element) => {
  if (!data.subscription_image) return;

  const processData = await INKIVE.DesygnerApi
    .post('payment/gateway/stripe/process', data.subscription_image);

  if (!processData && !processData.info) return;

  const paymentData = {
    ...data,
    ...processData,
    elementId: element.id
  };

  INKIVE.EditorFacade
    .handle('shopping_cart', 'on_payment', paymentData);
};

ImageSetter.prototype.isExternal = function (data) {
  const blob = INKIVE.Helpers.dataURLToBlob(data.source.url);

  return data.source.url && !blob &&
    !ImagesFactory.isAmazonImage(data.source.url) &&
    !data.source.url.match(/imageplaceholder\.svg$/) &&
    !data.source.url.match(/imageplaceholder_new\.svg$/) &&
    data.source.url.indexOf('shutterstock.com') === -1;
};

ImageSetter.prototype.updateFlipped = function (element, data, url) {
  const that = this;
  this.canvas.setFlipImage(element.id, data.flipped, url, undefined, () => {
    that.trigger('flipped', element.getAttribute('id'), data);
  });
};

ImageSetter.prototype.updateSourceBase64 = function (element, data) {
  const useSmartResize = typeof data.source.smart_load === 'undefined'
    ? true
    : data.source.smart_load;

  if (element.getAttribute('xlink:href') === data.source.base_64) {
    this.trigger('optimus_resolution', element.getAttribute('id'));
    return;
  }

  svgedit.utilities.assignAttributes(element, { 'xlink:href': data.source.base_64 });
  INKIVE.ImagesFactory.setImageUrl(element, null);

  if (useSmartResize) {
    const image = new Image();
    image.onload = _.bind(this._onImage, this, element, data, image);
    image.src = data.source.base_64;
  } else {
    this.trigger('source.base_64', element.getAttribute('id'), {
      source: { base_64: '<data>' }
    });
  }
};

ImageSetter.prototype.updateSourceUrl = function (element, data, skipEvent) {
  if (this.cancelImageLoading) {
    return;
  }

  const useSmartResize = typeof data.source.smart_load === 'undefined'
    ? true
    : data.source.smart_load;
  let triggerEvent = !skipEvent;
  let skipHistory = false;

  if (element.getAttribute('xlink:href') === INKIVE.Helpers.getCORSSecureUrl(data.source.url)) {
    this.trigger('optimus_resolution', element.getAttribute('id'));
    this.trigger('source.url', element.getAttribute('id'), {
      skipHistory: false,
      source: { url: !isDataURL(data.source.url) && data.source.url }
    });
    return;
  }

  if (INKIVE.ImagesFactory.getImageUrl(element) === null) {
    const originalHref = $(element).data('original-href');
    const originalHrefLoaded = originalHref && !isDataURL(originalHref);
    const href = $(element).attr('xlink:href');

    originalHrefLoaded && svgedit.utilities.assignAttributes(element, {
      'xlink:href': originalHref
    });

    INKIVE.ImagesFactory.setImageUrl(element, data.source.url);

    originalHrefLoaded && svgedit.utilities.assignAttributes(element, {
      'xlink:href': href
    });
  } else {
    const isSameRatio = data.newImageRatio ? Math.abs(data.newImageRatio - element.getAttribute('width') / element.getAttribute('height')) < 0.01 : true;
    const newImageOriginal = ImagesFactory.getOriginalUrl(data.source.url);
    const prevImageOriginal = ImagesFactory.getOriginalUrl(element.getAttribute('xlink:href'));
    const isSameSource = newImageOriginal === prevImageOriginal && data.external_source !== INKIVE.C.SHUTTERSTOCK && isSameRatio;
    const isSameImageOriginal = newImageOriginal === ImagesFactory.getOriginalUrl(INKIVE.ImagesFactory.getImageUrl(element));

    skipHistory = data.skipHistory || isSameImageOriginal;

    const props = INKIVE.DesignFacade.get(element.id) || {};
    const secureUrl = INKIVE.Helpers.getCORSSecureUrl(data.source.url);

    const flippedProperties = props.flipped;

    if (flippedProperties && (flippedProperties.horizontal || flippedProperties.vertical)) {
      this.updateFlipped(element, props, secureUrl);
    } else {
      svgedit.utilities.assignAttributes(element, { 'xlink:href': secureUrl });
    }

    if ((useSmartResize || data.source.external_resize || data.keep_box_size) && !isSameSource) {
      triggerEvent = false;
      const image = new Image();
      // image.crossOrigin = 'Anonymous';
      image.onload = _.bind(this._onImage, this, element, data, image);
      image.onerror = _.bind(this._onImageError, this, element, data, image);
      image.src = INKIVE.Helpers.getCORSSecureUrl(data.source.url);
    }
  }

  triggerEvent && !skipEvent && this.trigger('source.url', element.getAttribute('id'), {
    skipHistory,
    source: { url: !isDataURL(data.source.url) && data.source.url }
  });
};

ImageSetter.prototype._onImageError = function (element, data, image) {
  this._recoveryTry(element)
    .then(newImageUrl => {
      data.source.url = newImageUrl;
      this.updateSourceUrl(element, data);
      this._onImage(element, data, image);
    }).catch(() => {
      this.canvas.call('advice.show', 'image-processing-error');

      // Related to EDIT-2336
      // if (data.background_removed) return;
      // INKIVE.DesignFacade.handle(element.id, 'delete');
      // INKIVE.DesignFacade.handle(element.id, 'delete');
    });
};

ImageSetter.prototype._recoveryTry = function (element) {
  return new Promise((resolve, reject) => {
    const imageUrl = element.getAttribute('xlink:href');
    if (!imageUrl) return reject(new Error('No image url'));

    const sizes = Object.keys(imageConfig.sizes);
    const imageSize = sizes.find(size => imageUrl.includes(`/${size}/`));

    if (!imageSize) return reject(new Error('No image size'));
    if (imageSize === 'original') return reject(new Error('Image size is original'));

    const newImageUrl = imageUrl.replace(`/${imageSize}/`, '/original/');

    fetch(newImageUrl, { method: 'HEAD' })
      .then(response => {
        if (response.ok) {
          return resolve(newImageUrl);
        } else {
          return reject(new Error('Image not found'));
        }
      })
      .catch(error => {
        reject(error);
      });
  });
};

ImageSetter.prototype._onImage = function (element, data, image) {
  if (INKIVE.C.IS_MOBILE && element.dataset.hash) {
    element.dataset.hash = INKIVE.ImagesFactory.getImageDataHash(element);
  }

  if (data.keep_box_size) {
    updateElementDimensions.call(this, element);
  } else {
    updateImageBox.call(this);
  }

  const source = data?.source?.base_64
    ? { base_64: '<data>' }
    : { url: !isDataURL(data?.source?.url) && data?.source?.url };

  this.trigger(`source.${Object.keys(source)[0]}`, element.id, { source });

  function updateElementDimensions (element) {
    const elementDimensions = this.canvas.runExtensions('getLayoutBoxCoord', { layoutBox: element });

    const ratio = Math.max(
      elementDimensions.height / image.height,
      elementDimensions.width / image.width
    );
    const width = image.width * ratio;
    const height = image.height * ratio;
    const newSize = {
      width,
      height,
      x: elementDimensions.x - ((width - elementDimensions.width) / 2),
      y: elementDimensions.y - ((height - elementDimensions.height) / 2)
    };

    svgedit.utilities.assignAttributes(element, newSize);
  }

  function updateImageBox () {
    try {
      const clipPath = svgedit.utilities.getClipPath(element);
      const clipPathElem = clipPath && clipPath.childNodes[0];
      const layoutBox = clipPathElem || element;
      const bbox = layoutBox.getBBox();
      const imageRatio = image.width / image.height;
      const boxRatio = bbox.width / bbox.height;
      const flipVertical = imageRatio > boxRatio;
      const newSize = {
        width: flipVertical ? bbox.width : bbox.height * imageRatio,
        height: flipVertical ? bbox.width / imageRatio : bbox.height
      };

      this.updateSize(element, { size: newSize });
    } catch (error) {
      console.error(error);
    }
  }

  function updateElementSelectorToMatchSize (element) {
    const isSubElement = element.classList.contains('subelement');
    const selectedElement = isSubElement ? element.closest('.element') : element;
    const selectorManager = svgedit.select.getSelectorManager();
    const selector = selectorManager.requestSelector(selectedElement);
    selector.resize();
    this.canvas.runExtensions('selectedChanged');
  }
};

ImageSetter.prototype.onVersionData = function (element, data) {
  const size = this.getSize(element, data);
  const imageHash = INKIVE.ImagesFactory.getImageHash(element.id, data.source.url);

  element.dataset.hash = imageHash;

  element.classList.add('resizing');

  const cancelLoading = (id) => {
    if (id === element.id) this.cancelImageLoading = true;
    if (element) {
      element.classList.remove('resizing');
      delete element.dataset.hash;
    }
  };

  this.cancelImageLoading = false;
  this.trigger('optimize_resolution', element.getAttribute('id'));
  INKIVE.DesignFacade.on('delete', cancelLoading);

  const options = { isBackgroundRemoved: data.background_removed };

  const smartLoadCallBack = (status, optimizedImage) => {
    INKIVE.DesignFacade.off('delete', cancelLoading);

    const src = element.getAttribute('xlink:href');
    const isSameImage = data.external ||
      data.background_removed ||
      this.isExternal({ source: { url: src } }) ||
      INKIVE.C.IS_MOBILE ||
      ImagesFactory.getRightSizeUrl(src, size, true, options) === ImagesFactory.getRightSizeUrl(optimizedImage.src, size, true, options);

    const canUpdateImage = status === INKIVE.C.Status.success || data.source.url;
    data.source.url = status === INKIVE.C.Status.success
      ? optimizedImage.src
      : data.source.url;

    if (element.dataset.hash !== imageHash) {
      return;
    }

    element.classList.remove('resizing');
    delete element.dataset.hash;
    data.newImageRatio = optimizedImage.width / optimizedImage.height;

    (isSameImage && canUpdateImage && !this.cancelImageLoading) && this.updateSourceUrl(element, data);
    this.updateBackgroundRemoved(element, data);
  };

  ImagesFactory.smartLoad(
    data.source.url,
    size,
    smartLoadCallBack,
    data.external || data.background_removed,
    options
  );
};

ImageSetter.prototype.getSize = function (element, data) {
  return {
    width: data.size ? data.width : element.getAttribute('width'),
    height: data.size ? data.height : element.getAttribute('height')
  };
};

ImageSetter.prototype.resizeExternalPhoto = function (element, data) {
  const neededSize = getNeededSize.call(this);
  const matchedSize = getMatchedSize(data.source.url) || neededSize;
  const signedUrl = data.source.signed_url ?? false;
  const photoUrl = ImagesFactory.getOriginalUrl(data.source.url) || data.source.url;
  const params = {
    book_id: INKIVE.C.BOOK.id,
    photo_url: signedUrl ? Base64.encode(photoUrl) : photoUrl,
    platform: neededSize,
    skip_image_storage: data.external_source === INKIVE.C.SHUTTERSTOCK ? 'y' : 'n',
    signed_url: signedUrl ?? false
  };

  const url = INKIVE.C.BASE_URL + 'inkive/resizeexternalsearchphoto';

  $(element).addClass('resizing');
  this.trigger('resize_external', element.getAttribute('id'));

  const doPost = _.bind(function () {
    const uploadPromise = new Promise((resolve) => {
      $.api(url, 'POST', params, _.bind(function (result) {
        if (result.STATUS === 'success' && !!result.MESSAGE) {
          const imageData = {
            thumb: result.MESSAGE.replace(`/${matchedSize}/`, '/thumb/'),
            mobile: result.MESSAGE.replace(`/${matchedSize}/`, '/mobile/'),
            tab: result.MESSAGE.replace(`/${matchedSize}/`, '/tab/'),
            web: result.MESSAGE.replace(`/${matchedSize}/`, '/web/'),
            largeweb: result.MESSAGE.replace(`/${matchedSize}/`, '/largeweb/'),
            original: result.MESSAGE.replace(`/${matchedSize}/`, '/original/')
          };

          data.source.url = result.MESSAGE;

          ImagesFactory.addImageData(imageData, ImagesFactory.defaultAmazonDataNames);

          pendingS3Urls.set(element.getAttribute('id'), result.MESSAGE);
        } else {
          this.canvas.call('advice.show', 'image-processing-error');
        }

        this.onVersionData(element, data);
        resolve();
      }, this));
    });

    pendingExternalUploads.add(uploadPromise);
    uploadPromise.then(() => {
      pendingExternalUploads.delete(uploadPromise);
    });
  }, this);

  if (data.ab_id) {
    ImagesFactory.getExternalPhotoById({
      id: data.ab_id,
      type: 'ab'
    }, function (imgUrl) {
      params.photo_url = imgUrl;
      doPost();
    });
  } else {
    doPost();
  }

  function getNeededSize () {
    const size = this.getSize(element, data);
    const fackedUrl = 'https://static.desygner.com/thumb/';
    const fackedRightSizeUrl = ImagesFactory.getRightSizeUrl(fackedUrl, size);
    let neededSize = _.last(_.compact(fackedRightSizeUrl.split('/'))) || 'web';

    if (neededSize === 'thumb') neededSize = 'mobile';
    if (neededSize === 'original') neededSize = 'largeweb';

    return neededSize;
  }

  function getMatchedSize (url) {
    return ['original', 'largeweb', 'web', 'tab', 'mobile', 'thumb']
      .find(size => url.includes(`/${size}/`));
  }
};

ImageSetter.prototype.removeFlippedAttributesIfNewImage = function (element, data) {
  const originalUrl = $(element).attr('data-original-href');
  const newUrl = data.source.base_64 ? data.source.base_64 : data.source.url;

  if (originalUrl && !isDataURL(originalUrl) && originalUrl !== newUrl) {
    $(element).removeAttr('data-original-href');
    $(element).removeClass(INKIVE.C.FLIP_IMAGE.HORIZONTAL);
    $(element).removeClass(INKIVE.C.FLIP_IMAGE.VERTICAL);
  }
};

ImageSetter.prototype.removeNoPlaceholderClassIfReplacedImage = function (element, data) {
  if (!data.is_initial_load) {
    element.classList.remove('quick-delete');
  }
};

ImageSetter.prototype.updateBackgroundRemoved = function (element, data) {
  data.background_removed
    ? element.setAttribute('data-background-removed', 'true')
    : element.removeAttribute('data-background-removed');
};

ImageSetter.prototype.refreshPlaceholderButtons = function (element, data) {
  if (!data.img_frame) return;

  const elems = this.canvas.getSelectedElems();
  element = element.classList.contains('subelement')
    ? element.parentElement
    : element;

  if (elems.length === 1 && elems[0] === element) {
    this.canvas.clearSelection();
    this.canvas.addToSelection([element]);
  }
};

ImageSetter.waitForPendingExternalUploads = function (timeout = 5000) {
  const waitPromise = pendingExternalUploads.size === 0
    ? Promise.resolve()
    : Promise.race([
      Promise.all([...pendingExternalUploads]),
      new Promise(resolve => setTimeout(resolve, timeout))
    ]);

  return waitPromise.then(() => {
    pendingS3Urls.forEach((s3Url, elementId) => {
      const element = document.getElementById(elementId);
      if (!element) return;

      const currentHref = element.getAttribute('xlink:href');
      if (currentHref && !ImagesFactory.isAmazonImage(currentHref)) {
        svgedit.utilities.assignAttributes(element, {
          'xlink:href': INKIVE.Helpers.getCORSSecureUrl(s3Url)
        });
      }
    });
    pendingS3Urls.clear();
  });
};
